// Dependencies
import { ChangeEvent, FC, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Box, CircularProgress, Grid, Pagination, Stack, Typography, FormControl, Select, MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import moment from 'moment';

// Components
import { DocActions, NewDocDialog } from '../../components';

// Context
import { AppContext } from '../../context';

// Resource
import { DisplayMode } from '../../resource';

// Utils
import { useUrlParams, FilterParams } from '../../utils/urlParams';
import { useDebounce } from '../../hooks/useDebounce';

// Styles
import styles from './styles';
import {useToastContext} from '../../components/Toast/toastContext';

// Sort options type
type SortOption = 'name_asc' | 'name_desc' | 'createdAt_asc' | 'createdAt_desc' | 'updatedAt_asc' | 'updatedAt_desc';

/** Listing date: stored published_at only. Never fall back to "now" (moment(undefined) would). */
function formatListingPublishedAt(value?: string | Date | null): string {
  if (value == null || value === '') return '—';
  if (value instanceof Date && Number.isNaN(value.getTime())) return '—';
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw && !(value instanceof Date)) return '—';
  if (/^invalid/i.test(raw)) return '—';

  const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  const parsed = isoDay
    ? moment.utc(isoDay, 'YYYY-MM-DD', true)
    : moment.utc(raw || value, ['YYYY-MM-DD', 'DD.MM.YYYY', 'D.M.YYYY', moment.ISO_8601], true);

  const fallback = parsed.isValid() ? parsed : moment.utc(value);
  if (!fallback.isValid()) return '—';
  const dateOnly =
    Boolean(isoDay) ||
    /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(raw) ||
    (fallback.hours() === 0 && fallback.minutes() === 0 && fallback.seconds() === 0);
  const text = dateOnly
    ? fallback.format('DD MMMM YYYY')
    : fallback.format('DD MMMM YYYY / HH:mm:ss');
  return /invalid/i.test(text) ? '—' : text;
}

// Export home page
export const HomePage: FC = () => {
  const { activeToast } = useToastContext();
  
  // URL params hook
  const { updateUrlParams, updateUrlParamsAfterApiResponse, initializeFilters, getUrlParams, restoreFromStorage } = useUrlParams('dashboard');
  
  // States
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(9);
  const [sortOption, setSortOption] = useState<SortOption>('createdAt_desc');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get context
  const {
    account,
    displayMode,
    dialogs,
    docTotalCount,
    documents,
    fetchDocuments,
    documentsLoading,
    showDeleted,
    setShowDeleted,
    showBackups,
    setShowBackups,
    retentionConfig,
    search,
    setSearch,
  } = useContext(AppContext);
  const debouncedSearch = useDebounce(search);

  const emptyStateText = useMemo(() => {
    const keyword = (search ?? '').trim();
    if (keyword && (documents?.length ?? 0) === 0) {
      return `No document with search keyword: ${keyword}`;
    }
    if (showBackups) return 'No backups yet';
    if (showDeleted) return 'No archived documents';
    return 'No Document';
  }, [search, documents, showBackups, showDeleted]);

  // Initialize filters from URL or localStorage
  useEffect(() => {
    console.log('Initializing Factsheets filters...');
    
    // Get current URL parameters
    const urlParams = getUrlParams();
    // console.log('Current URL params:', urlParams);
    
    // Get stored filters
    const storedParams = restoreFromStorage();
    // console.log('Stored params:', storedParams);
    
    // Determine which parameters to use
    let finalParams;
    
    if (Object.keys(urlParams).length > 0) {
      // URL has parameters - use them
      // console.log('Using URL parameters');
      finalParams = urlParams;
    } else if (Object.keys(storedParams).length > 0) {
      // No URL params but stored params exist - use stored params
      // console.log('Using stored parameters');
      finalParams = storedParams;
    } else {
      // No stored params - use defaults
      // console.log('Using default parameters');
      finalParams = {
        page: 1,
        perPage: displayMode === DisplayMode.Module ? 9 : 15,
        sort: 'createdAt_desc',
        archived: false,
        backups: false,
      };
    }
    
    // console.log('Final params to apply:', finalParams);
    
    // Apply the final parameters
    setCurrentPage(finalParams.page || 1);
    setPerPage(finalParams.perPage || (displayMode === DisplayMode.Module ? 9 : 15));
    setSortOption(finalParams.sort as SortOption || 'createdAt_desc');
    
    if (finalParams.archived !== undefined) {
      setShowDeleted(finalParams.archived);
    }
    if (finalParams.backups !== undefined) {
      setShowBackups(!!finalParams.backups);
    }
    
    // Handle search parameter - only restore from URL params, not localStorage
    if (finalParams.search !== undefined && finalParams.search !== '') {
      setSearch(finalParams.search);
    }
    
    setIsInitialized(true);
  }, [displayMode]);

  // Remove the problematic synchronization effect that was reverting changes
  // The showDeleted state should only be set during initialization, not continuously synced

  // Calc total page
  const totalPage = useMemo(() => {
    return Math.ceil(docTotalCount / perPage);
  }, [docTotalCount, perPage]);

  // Parse sort option to get field and order
  const { sortField, sortOrder } = useMemo(() => {
    const [field, order] = sortOption.split('_');
    return { sortField: field as 'name' | 'createdAt' | 'updatedAt', sortOrder: order as 'asc' | 'desc' };
  }, [sortOption]);

  // Sort documents
  const sortedDocuments = useMemo(() => {
    if (!documents) return [];
    
    return [...documents].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt).getTime();
          bValue = new Date(b.updatedAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [documents, sortField, sortOrder]);

  // Page change handler
  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    updateUrlParams({ page });
  };

  // Sort option change handler
  const handleSortOptionChange = (event: SelectChangeEvent<SortOption>) => {
    const newSortOption = event.target.value as SortOption;
    setSortOption(newSortOption);
    updateUrlParams({ sort: newSortOption });
  };

  const fetch = async () => {
    if (!account?.id) return;
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    await fetchDocuments(
      account.id,
      currentPage,
      perPage,
      showDeleted,
      sortField,
      sortOrder,
      debouncedSearch,
      (msg?: string) => {
        if (requestId === latestRequestId.current) {
          if (msg) {
            activeToast({
              type: 'error',
              text: msg || 'Failed to fetch documents',
            });
          }
        }
      },
      controller.signal,
      requestId
    );
  }

  // Update URL params when showDeleted / showBackups changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
      updateUrlParams({ page: 1, archived: showDeleted, backups: showBackups });
    }
  }, [showDeleted, showBackups, isInitialized]);



  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
      updateUrlParams({ page: 1 });
    }
  }, [isInitialized]);

  // On mounted - only fetch after initialization
  useEffect(() => {
    if (isInitialized && account?.id) {
      fetch();
    }
  }, [account?.id, perPage, currentPage, showDeleted, showBackups, isInitialized]);

  // On search changed - update URL after successful API response
  useEffect(() => {
    if (isInitialized && account?.id && debouncedSearch !== undefined) {
      fetch();
      setTimeout(() => {
        updateUrlParamsAfterApiResponse({ search: debouncedSearch });
      }, 100);
    }
  }, [debouncedSearch, isInitialized]);

  // On sort option changed - call API
  useEffect(() => {
    if (isInitialized && account?.id) {
      fetch();
    }
  }, [sortOption, isInitialized]);

  // On display mode changed
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
      const newPerPage = displayMode === DisplayMode.Module ? 9 : 15;
      setPerPage(newPerPage);
      updateUrlParams({ page: 1, perPage: newPerPage });
    }
  }, [displayMode, isInitialized]);

  // Return home page
  return (
    <>
      <Box sx={styles.container}>
        {documentsLoading ? (
          <CircularProgress />
        ) : documents?.length > 0 ? (
          <Stack width="100%" alignItems="center" spacing={{ xs: 48, md: 64, lg: 128 }}>
            <Box sx={styles.paginationContainer}>
              {totalPage > 1 && (
                <Pagination page={currentPage} count={totalPage} siblingCount={0} onChange={handlePageChange} />
              )}
              <Box sx={styles.sortContainer}>
                <FormControl size="small" sx={styles.sortField}>
                  <Select
                    value={sortOption}
                    onChange={handleSortOptionChange}
                    displayEmpty
                  >
                    <MenuItem value="name_asc">Name ↓</MenuItem>
                    <MenuItem value="name_desc">Name ↑</MenuItem>
                    <MenuItem value="createdAt_asc">Created Date ↓</MenuItem>
                    <MenuItem value="createdAt_desc">Created Date ↑</MenuItem>
                    <MenuItem value="updatedAt_asc">Updated Date ↓</MenuItem>
                    <MenuItem value="updatedAt_desc">Updated Date ↑</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {displayMode === DisplayMode.Module && (
              <Grid sx={styles.cardBox} container columns={12} spacing={{ xs: 32, md: 56, lg: 82 }}>
                {sortedDocuments.map((doc, dIndex) => {
                  return (
                    <Grid item xs={12} md={6} xl={4} key={dIndex}>
                      <Box sx={styles.card}>
                        <Box sx={styles.cardHeader}>
                          <Box>
                            <Typography variant="title" sx={{ lineBreak: 'anywhere' }}>{doc.name}</Typography>
                            <Typography variant="body2">
                              {showBackups
                                ? 'Backup — restore as master or permanently delete'
                                : showDeleted
                                ? 'Archived — restore or permanently delete (no auto-purge)'
                                : formatListingPublishedAt(doc.published_at)}
                            </Typography>
                          </Box>
                          <DocActions
                            doc={doc}
                            direction="vertical"
                            archived={showDeleted}
                            backups={showBackups}
                            fetch={fetch}
                          />
                        </Box>
                        <Box sx={styles.thumbnail}>
                          {doc.isAutomated && (
                            <Box sx={styles.automationBadge}>
                              <Tooltip title="Automated Factsheet" arrow placement="top">
                                <AutoAwesomeIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                              </Tooltip>
                            </Box>
                          )}
                          <img src={doc.thumbnail_url} alt={doc.thumbnail_url}/>
                        </Box>
                      </Box>
                    </Grid>
                  )
                })}
              </Grid>
            )}
            {displayMode === DisplayMode.List && (
              <Stack width="100%" spacing={20}>
                {sortedDocuments.map((doc, dIndex) => (
                  <Box key={dIndex} sx={styles.listItem}>
                    <Box>
                      <Typography variant="title">{doc.name}</Typography>
                      <Typography mt={12} variant="body2">
                        {showBackups
                          ? 'Backup — restore as master or permanently delete'
                          : showDeleted
                          ? 'Archived — restore or permanently delete (no auto-purge)'
                          : formatListingPublishedAt(doc.published_at)}
                      </Typography>
                    </Box>
                    <Box sx={styles.listAction}>
                      <DocActions
                        doc={doc}
                        archived={showDeleted}
                        backups={showBackups}
                        fetch={fetch}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        ) : (
          <Typography variant="title" align="center">
            {emptyStateText}
          </Typography>
        )}
      </Box>
      {dialogs.newDoc.visible && <NewDocDialog open={dialogs.newDoc.visible} />}
    </>
  );
};
